import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaCog, FaBell } from 'react-icons/fa';
import CoinDisplay from '../UI/CoinDisplay';
import Avatar from '../UI/Avatar';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

/**
 * Barre de navigation supérieure
 * Utilisée dans les pages de jeu et sous-pages
 */
function Navbar({
  title,
  showBack = false,
  showCoins = true,
  showAvatar = false,
  showSettings = false,
  onBack,
  rightContent,
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if (!user) return;
    const fetchNotifs = () => {
      api.get('/notifications?limit=10')
        .then(({ data }) => {
          setUnreadCount(data.unreadCount || 0);
          setNotifications(data.notifications || []);
        })
        .catch(() => {});
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (_) {}
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="flex items-center justify-between px-4 py-3 w-full">
      {/* Gauche */}
      <div className="flex items-center gap-3">
        {showBack && (
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={handleBack}
            className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <FaArrowLeft size={14} className="text-white" />
          </motion.button>
        )}
        {showAvatar && (
          <Avatar
            user={user}
            size="sm"
            onClick={() => navigate(`/profile/${user?.id}`)}
            className="cursor-pointer"
          />
        )}
      </div>

      {/* Centre - titre */}
      {title && (
        <h1
          className="text-lg font-bold uppercase tracking-widest"
          style={{
            fontFamily: "'Rajdhani', sans-serif",
            color: title.includes('CHRONO') ? '#FFB81C' : title.includes('OM') ? '#003DA5' : '#FFB81C',
          }}
        >
          {title}
        </h1>
      )}

      {/* Droite */}
      <div className="flex items-center gap-2">
        {rightContent}
        {user && (
          <div className="relative">
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => {
                setShowNotifs(!showNotifs);
                if (!showNotifs && unreadCount > 0) markAllRead();
              }}
              className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors relative"
            >
              <FaBell size={14} className="text-white" />
              {unreadCount > 0 && (
                <span
                  style={{
                    position: 'absolute', top: -2, right: -2,
                    width: 16, height: 16, borderRadius: '50%',
                    background: '#ef4444', color: '#fff',
                    fontSize: 10, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </motion.button>
            {showNotifs && (
              <div
                style={{
                  position: 'absolute', right: 0, top: 44,
                  width: 280, background: '#003DA5',
                  border: '1px solid rgba(255,184,28,0.3)',
                  borderRadius: 12, zIndex: 9999,
                  boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
                  maxHeight: 320, overflowY: 'auto',
                }}
              >
                <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <span style={{ color: '#FFB81C', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: '0.85rem' }}>
                    NOTIFICATIONS
                  </span>
                </div>
                {notifications.length === 0 ? (
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', textAlign: 'center', padding: '16px' }}>
                    Aucune notification
                  </p>
                ) : (
                  notifications.map(n => (
                    <div
                      key={n.id}
                      style={{
                        padding: '10px 14px',
                        borderBottom: '1px solid rgba(255,255,255,0.06)',
                        background: n.is_read ? 'transparent' : 'rgba(255,184,28,0.08)',
                      }}
                    >
                      <p style={{ color: '#fff', fontSize: '0.8rem', margin: 0, fontWeight: n.is_read ? 400 : 600 }}>
                        {n.title || n.type}
                      </p>
                      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', margin: '2px 0 0' }}>
                        {n.message}
                      </p>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}
        {showCoins && (
          <CoinDisplay coins={user?.score || user?.coins || 0} />
        )}
        {showSettings && (
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => navigate('/profile/' + user?.id)}
            className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <FaCog size={14} className="text-white" />
          </motion.button>
        )}
      </div>
    </div>
  );
}

export default Navbar;

